import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface UIState {
  isLoading: boolean;
  error: string | null;
  notifications: Notification[];
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  rulesModalOpen: boolean;
  ageModalOpen: boolean;
  changePasswordModalOpen: boolean;
  editStakeOpen: boolean;
  loginModalOpen: boolean;
  liveTvEnabled: boolean;
}

const initialState: UIState = {
  isLoading: false,
  error: null,
  notifications: [],
  sidebarOpen: true,
  sidebarCollapsed: false,
  rulesModalOpen: false,
  ageModalOpen: false,
  changePasswordModalOpen: false,
  editStakeOpen: false,
  loginModalOpen: false,
  liveTvEnabled: false,
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotification: (state) => {
      state.notifications = [];
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setRulesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.rulesModalOpen = action.payload;
    },
    toggleRulesModalOpen: (state) => {
      state.rulesModalOpen = !state.rulesModalOpen;
    },
    setAgeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ageModalOpen = action.payload;
    },
    toggleAgeModalOpen: (state) => {
      state.ageModalOpen = !state.rulesModalOpen;
    },
    setChangePasswordModalOpen: (state, action: PayloadAction<boolean>) => {
      state.changePasswordModalOpen = action.payload;
    },
    toggleChangePasswordModalOpen: (state) => {
      state.changePasswordModalOpen = !state.changePasswordModalOpen;
    },
    setEditStake: (state, action: PayloadAction<boolean>) => {
      state.editStakeOpen = action.payload;
    },
    toggleEditStake: (state) => {
      state.editStakeOpen = !state.editStakeOpen;
    },
    setLoginModalOpen: (state, action: PayloadAction<boolean>) => {
      // open/close global login modal
      // when true, UI should render the login modal
      // when false, hide it
      // default: false
      // note: component consuming this must dispatch to change
      // behavior
      (state as any).loginModalOpen = action.payload;
    },
    setLiveTvEnabled: (state, action: PayloadAction<boolean>) => {
      state.liveTvEnabled = action.payload;
    },
    toggleLiveTv: (state) => {
      state.liveTvEnabled = !state.liveTvEnabled;
    },
  },
});

export const {
  setLoading,
  setError,
  clearError,
  setNotification,
  removeNotification,
  clearNotification,
  setSidebarOpen,
  toggleSidebar,
  setSidebarCollapsed,
  toggleSidebarCollapsed,
  setRulesModalOpen,
  toggleRulesModalOpen,
  setAgeModalOpen,
  toggleAgeModalOpen,
  setChangePasswordModalOpen,
  toggleChangePasswordModalOpen,
  setEditStake,
  toggleEditStake,
  setLoginModalOpen,
  setLiveTvEnabled,
  toggleLiveTv,
} = uiSlice.actions;
